// Billing status banners (Track B3).
//
// trialing   → dismissible-per-session pilot countdown. Gentle: the
//              pilot is a gift, not a meter running out.
// past_due   → persistent, non-dismissible: the dashboard is
//              read-only and this is the way back. (The worker
//              already reports an expired trial as past_due.)
// canceled   → same treatment with "Reactivate" phrasing.
// grandfathered / active / pending / undefined → nothing.
//
// Never blocks anything itself — enforcement lives in the worker
// (lib/billing.ts). Volunteers never see this: it renders only
// inside the admin shell.

import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useParish } from "../lib/parishContext";

const DISMISS_KEY = "sacristy.trial-banner-dismissed.v1";

function daysLeft(trialEndsAt: string): number {
  return Math.max(0, Math.ceil((Date.parse(trialEndsAt) - Date.now()) / 86_400_000));
}

export default function BillingBanner() {
  const { parish } = useParish();
  const location = useLocation();
  const [dismissed, setDismissed] = useState(
    () => window.sessionStorage.getItem(DISMISS_KEY) === "1",
  );

  if (location.pathname.startsWith("/holy-week/print")) return null;

  if (parish.status === "trialing" && parish.trial_ends_at) {
    if (dismissed) return null;
    const n = daysLeft(parish.trial_ends_at);
    return (
      <aside className="app-banner">
        <span>
          Free pilot — {n} {n === 1 ? "day" : "days"} left.{" "}
          <Link className="app-banner__link" to="/settings/billing">
            Add billing anytime
          </Link>
          .
        </span>
        <button
          type="button"
          className="app-banner__dismiss"
          aria-label="Dismiss for this session"
          onClick={() => {
            window.sessionStorage.setItem(DISMISS_KEY, "1");
            setDismissed(true);
          }}
        >
          ×
        </button>
      </aside>
    );
  }

  if (parish.status === "past_due") {
    return (
      <aside className="app-banner app-banner--urgent">
        <span>
          Your free pilot has ended. Your schedule stays visible; the assistant is paused.{" "}
          <Link className="app-banner__link" to="/settings/billing">
            Add billing
          </Link>
        </span>
      </aside>
    );
  }

  if (parish.status === "canceled") {
    return (
      <aside className="app-banner app-banner--urgent">
        <span>
          Your subscription is canceled. Your schedule stays visible.{" "}
          <Link className="app-banner__link" to="/settings/billing">
            Reactivate
          </Link>
        </span>
      </aside>
    );
  }

  return null;
}
